import React, { useState } from 'react';
import { X, Key, AlertTriangle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { usersApi, User } from '../../lib/api';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  const handleResetPassword = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await usersApi.resetPassword(user.id, {
        newPassword: '', // Not used by backend
        confirmPassword: '' // Not used by backend
      });
      
      if (response.data.success) {
        setTempPassword(response.data.data!.tempPassword);
        setEmail(response.data.data!.email);
        setSuccess(true);
        toast.success(`Password reset successfully for ${user.firstName} ${user.lastName}`);
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err: any) {
      console.error('Error resetting password:', err);
      const errorMessage = err.response?.data?.message || 'Failed to reset password';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setTempPassword('');
    setEmail('');
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
              <Key className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
              <p className="text-sm text-gray-600 mt-1">
                {user.firstName} {user.lastName} ({user.role.toLowerCase()})
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!success ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <h3 className="text-sm font-medium text-amber-800">Warning</h3>
                  <p className="text-sm text-amber-700">
                    This will reset the user's password and generate a new temporary password. 
                    The user will need to change it on their next login.
                  </p>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Are you sure you want to reset the password for <strong>{user.firstName} {user.lastName}</strong>?
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Password Reset Successful</h3>
                  <p className="text-sm text-green-700">
                    The password has been reset successfully.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-sm text-gray-900">{email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temporary Password
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-sm font-mono text-gray-900">{tempPassword}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Share this temporary password with the user. They must change it on their next login.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          {!success ? (
            <>
              <button
                onClick={handleClose}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="btn-primary bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="btn-primary"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal; 